import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock3, Leaf, Target } from "lucide-react";
import { api } from "@/lib/api";

const fmtMonth = (key) => new Date(`${key}-15T12:00:00Z`).toLocaleString("en-US", { month: "long", year: "numeric" });
const fmtDate = (day) => new Date(`${day}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });

function Bar({ id, label, value, goal, unit, Icon, color }) {
  const pct = Math.min(100, Math.round((value / goal) * 100));
  const hit = value >= goal;
  return (
    <div data-testid={`tb-goal-${id}`}>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-zinc-400 flex items-center gap-1.5"><Icon size={12} className={color} />{label}</span>
        <span className={`tabular-nums ${hit ? "text-emerald-300" : "text-white"}`} data-testid={`tb-goal-${id}-value`}>
          {value.toLocaleString()} / {goal.toLocaleString()} {unit}{hit && " ✓"}
        </span>
      </div>
      <div className="h-1.5 bg-white/10 overflow-hidden"><div className={`h-full transition-[width] duration-700 ${hit ? "bg-emerald-400" : "bg-yellow-400"}`} style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

function statusFor(p) {
  if (p.kickoff_exempt) return { text: `Kickoff winner — exempt from monthly goals through ${fmtDate(p.kickoff_exempt_through)}.`, cls: "text-yellow-200" };
  if (p.admin_granted) return { text: "Awarded by My Panda X — not subject to the monthly review.", cls: "text-yellow-200" };
  if (p.qualified) return { text: p.holder ? "Goals met — you keep the Top Badge at month-end review." : "Goals met — you earn the Top Badge at month-end review.", cls: "text-emerald-300" };
  if (p.holder) return { text: "Hit both goals before month-end to keep your Top Badge.", cls: "text-rose-300" };
  return { text: "Hit both goals before month-end to earn the Top Badge.", cls: "text-zinc-400" };
}

// Creator's live progress toward this month's goals (hours · 🎋 · days left).
export function TopBadgeGoalProgress({ p, compact = false }) {
  const st = statusFor(p);
  return (
    <div className="space-y-3" data-testid="tb-goal-progress">
      <div className="flex items-center justify-between text-xs text-zinc-400">
        <span data-testid="tb-goal-month">{fmtMonth(p.month)}</span>
        <span className="flex items-center gap-1" data-testid="tb-goal-days-left"><Clock3 size={12} />{p.days_left} day{p.days_left === 1 ? "" : "s"} left</span>
      </div>
      <Bar id="hours" label="Streaming hours" value={p.hours} goal={p.hours_goal} unit="h" Icon={Target} color="text-yellow-300" />
      <Bar id="bamboo" label="Bamboo earned from gifts" value={p.bamboo} goal={p.bamboo_goal} unit="🎋" Icon={Leaf} color="text-emerald-300" />
      <div className={`text-xs ${st.cls}`} data-testid="tb-goal-status">{st.text}</div>
      {!compact && <Link to="/top-badge" className="text-xs text-rose-400 hover:text-rose-300" data-testid="tb-goal-link">Top Badge program →</Link>}
    </div>
  );
}

// Creator Dashboard card.
export default function TopBadgeGoalCard() {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/top-badge/monthly/me").then((r) => setP(r.data)).catch(() => {}); }, []);
  if (!p) return null;
  return (
    <section className="card-surface p-5" data-testid="tb-goal-card">
      <div className="flex items-center gap-3 mb-4">
        <img src="/vip-art/top_badge.png" alt="" className="w-10 h-10 object-contain" draggable={false} />
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-yellow-300">Top Badge · monthly goals</div>
          <div className="text-sm text-zinc-300">{p.active ? "Stream 25 hours and earn 50,000 🎋 from gifts this month." : `Monthly goals start ${fmtMonth(p.first_month)} — here's your pace.`}</div>
        </div>
      </div>
      <TopBadgeGoalProgress p={p} />
    </section>
  );
}
